/**
 * Circuul app/web CPI (cents) by campaign influencer tier + creator subscription.
 * Mirrors boxofix/services/circuulCpaRates.js
 */
export const CPI_CENTS_BY_TIER = {
    micro: { unsubscribed: 10, subscribed: 15 },
    mid: { unsubscribed: 20, subscribed: 25 },
    macro: { unsubscribed: 40, subscribed: 50 },
    'top-tier': { unsubscribed: 70, subscribed: 100 },
} as const;

export type CircuulCpaTier = keyof typeof CPI_CENTS_BY_TIER;

/** Installs used for public “possible earnings” estimates on campaign detail. */
export const APP_INSTALL_EARNINGS_BENCHMARK = 100;

/** Max installs/visits on the creator earnings calculator. */
export const APP_INSTALL_CALCULATOR_MAX = 1000;

export const TIER_FOLLOWER_BANDS: ReadonlyArray<{
    id: CircuulCpaTier;
    label: string;
    minFollowers: number;
    reach: string;
}> = [
    { id: 'micro', label: 'Micro', minFollowers: 300, reach: '300–50k' },
    { id: 'mid', label: 'Mid-tier', minFollowers: 50_000, reach: '50k–200k' },
    { id: 'macro', label: 'Macro', minFollowers: 200_000, reach: '200k–1M' },
    { id: 'top-tier', label: 'Top-tier', minFollowers: 1_000_000, reach: '1M+' },
];

export function normalizeCircuulCpaTier(tier?: string | null): CircuulCpaTier {
    const t = String(tier || 'micro').toLowerCase();
    if (t === 'top' || t === 'top_tier' || t === 'toptier') return 'top-tier';
    if (t in CPI_CENTS_BY_TIER) return t as CircuulCpaTier;
    return 'micro';
}

/** Map a follower/reaction count to the CPI tier band. */
export function tierFromFollowerCount(followers?: number | null): CircuulCpaTier {
    const n = Math.max(0, Math.floor(Number(followers) || 0));
    let matched: CircuulCpaTier = 'micro';
    for (const band of TIER_FOLLOWER_BANDS) {
        if (n >= band.minFollowers) matched = band.id;
    }
    return matched;
}

export function clampInstallCount(value: number, max = APP_INSTALL_CALCULATOR_MAX): number {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(max, Math.round(value)));
}

export function cpaCentsForTier(
    tier?: string | null,
    { creatorSubscribed = false }: { creatorSubscribed?: boolean } = {},
): number {
    const row = CPI_CENTS_BY_TIER[normalizeCircuulCpaTier(tier)];
    return creatorSubscribed ? row.subscribed : row.unsubscribed;
}

export function formatCpaCents(cents: number): string {
    if (cents >= 100) {
        return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
    }
    return `${cents}¢`;
}

export function formatCpaDollarsFromCents(cents: number): string {
    const dollars = cents / 100;
    return `$${dollars.toLocaleString(undefined, {
        minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    })}`;
}

export interface IAppInstallEarningsEstimate {
    tier: CircuulCpaTier;
    benchmarkInstalls: number;
    creatorSubscribed: boolean;
    cpiUnsubscribedCents: number;
    cpiSubscribedCents: number;
    /** CPI for the viewer’s current subscription status */
    cpiAppliedCents: number;
    earningsUnsubscribedCents: number;
    earningsSubscribedCents: number;
    /** Benchmark earnings at applied CPI */
    earningsAppliedCents: number;
    extraIfSubscribedCents: number;
}

export function estimateAppInstallEarnings(opts: {
    influencerTier?: string | null;
    creatorSubscribed?: boolean;
    benchmarkInstalls?: number;
}): IAppInstallEarningsEstimate {
    const tier = normalizeCircuulCpaTier(opts.influencerTier);
    const benchmarkInstalls = clampInstallCount(opts.benchmarkInstalls ?? APP_INSTALL_EARNINGS_BENCHMARK);
    const creatorSubscribed = Boolean(opts.creatorSubscribed);
    const cpiUnsubscribedCents = cpaCentsForTier(tier, { creatorSubscribed: false });
    const cpiSubscribedCents = cpaCentsForTier(tier, { creatorSubscribed: true });
    const cpiAppliedCents = creatorSubscribed ? cpiSubscribedCents : cpiUnsubscribedCents;
    const earningsUnsubscribedCents = cpiUnsubscribedCents * benchmarkInstalls;
    const earningsSubscribedCents = cpiSubscribedCents * benchmarkInstalls;
    return {
        tier,
        benchmarkInstalls,
        creatorSubscribed,
        cpiUnsubscribedCents,
        cpiSubscribedCents,
        cpiAppliedCents,
        earningsUnsubscribedCents,
        earningsSubscribedCents,
        earningsAppliedCents: cpiAppliedCents * benchmarkInstalls,
        extraIfSubscribedCents: earningsSubscribedCents - earningsUnsubscribedCents,
    };
}
